import { Switch, cn } from "@sagot/ui";
import { useId, type ReactNode } from "react";

interface SettingSwitchProps {
  title: string;
  hint?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Accessible name when it differs from the visible title. */
  switchLabel?: string;
  className?: string;
  children?: ReactNode;
}

/** Title + hint on the left, switch on the right; `children` render below while switched on. */
export function SettingSwitch({ title, hint, checked, onCheckedChange, disabled, switchLabel, className, children }: SettingSwitchProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div id={`${id}-title`} className="text-sm font-semibold">
            {title}
          </div>
          {hint ? (
            <div id={hintId} className="mt-0.5 text-xs text-fg-subtle">
              {hint}
            </div>
          ) : null}
        </div>
        <Switch
          className="mt-0.5"
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-label={switchLabel ?? title}
          aria-describedby={hintId}
        />
      </div>
      {checked && children ? children : null}
    </div>
  );
}

export function ControlRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        {hint ? <div className="text-xs text-fg-subtle">{hint}</div> : null}
      </div>
      {children}
    </div>
  );
}
